/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { AVComponent } from '@/components/Videoplayer/AVDisplay';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import PeakDataProcessor from '@/lib/PeakDataProcessor';
import { useSocketIo } from '@/contexts/SocketIoContext';

export interface AudioLoader {
  onAvComponentReady(data: AVComponent): void;
  handleStop(): void;
  peakDataProcessor: PeakDataProcessor;
}

interface PeakDataSegment {
  startTimeMs: number; // Start time in milliseconds, rounded down to 1/16s
  endTimeMs: number; // End time in milliseconds, rounded up to 1/16s
  peaks: number[]; // Array of peak values
  segmentNumber?: number;
  durationUs?: number;
}

export interface UseAudioLoaderOptions {
  /**
   * When true, skip the socket peak_data ingestion and load `audioUrlOverride`
   * directly into wavesurfer so the rendered waveform is the segment's audio
   * only. Used for the speaker view, where the worker pipeline would
   * otherwise stream peaks for the full show.
   */
  isSpeaker?: boolean;
  /**
   * Direct audio URL to feed to wavesurfer in speaker mode. Should be the
   * signed URL of the speaker's segment MP4 — wavesurfer decodes the audio
   * track via the Web Audio API and produces real peaks for it.
   */
  audioUrlOverride?: string;
  /**
   * Speaker mode only: fires after wavesurfer finishes decoding the
   * swapped-in segment audio. The waveform's duration is only correct
   * once this resolves, so consumers should re-trigger any region/range
   * rendering tied to that duration here. Without it, regions added with
   * the dummy 5-min duration fall off-screen and clicks miss.
   *
   * The decoded duration (in seconds) is forwarded so callers can use it
   * as an authoritative end time for the segment row even before any
   * separate <audio> probe lands.
   */
  onSpeakerWaveReady?(durationSec: number): void;
}

export function useAudioLoader(streamId: string, options: UseAudioLoaderOptions = {}): AudioLoader {
  const { isSpeaker = false, audioUrlOverride, onSpeakerWaveReady } = options;
  const avComponentRef = useRef<AVComponent | null>(null);
  const pdp: PeakDataProcessor = useMemo(() => new PeakDataProcessor(), []);
  const { socketPromise } = useSocketIo();
  const subscribedRef = useRef<boolean>(false);
  const needsUpdateRef = useRef<boolean>(false);
  const speakerLoadedUrlRef = useRef<string | null>(null);
  const onSpeakerWaveReadyRef = useRef<UseAudioLoaderOptions['onSpeakerWaveReady']>(undefined);
  onSpeakerWaveReadyRef.current = onSpeakerWaveReady;

  // Set up WebSocket listener when socket becomes available — but only for
  // editor accounts. Speakers must not ingest peak_data, otherwise the
  // wavesurfer would render the full-show waveform instead of just the
  // segment they're allowed to see.
  useEffect(() => {
    if (isSpeaker) return;
    if (socketPromise && !subscribedRef.current) {
      subscribedRef.current = true;

      // Listen for peak data events using onAny to catch all events
      socketPromise.onAny((eventName: string, ...args: unknown[]) => {
        if (eventName === 'peak_data') {
          const peakData = args[0] as PeakDataSegment;
          pdp.processPeakDataSegment(peakData);

          // Update wavesurfer if available, otherwise mark as needing update
          if (avComponentRef.current?.audioRef) {
            pdp.updateWavesurfer(avComponentRef.current.audioRef).catch(console.error);
          } else {
            needsUpdateRef.current = true;
          }
        }
      });
    }

    return () => {
      if (socketPromise) {
        socketPromise.offAny();
        subscribedRef.current = false;
      }
    };
  }, [socketPromise, pdp, isSpeaker]);

  const initWaveform = useCallback(async () => {
    if (!avComponentRef.current) return;
    if (!streamId) return;

    const wavesurfer = avComponentRef.current.audioRef;
    await pdp.updateWavesurfer(wavesurfer);
  }, [streamId, pdp]);

  // For speakers: once wavesurfer is mounted, load the segment audio URL
  // and let wavesurfer's `decode` event tell us the AudioBuffer's true
  // duration. We can't rely on:
  //   - The initial dummy load's `options.duration = 300` — sticks unless
  //     overridden, so naked `getDuration()` returns 300.
  //   - A standalone `<audio>` probe — its `duration` reports the video
  //     container's length, which can be longer than the audio stream's
  //     actual decoded length when the container has both video and
  //     audio tracks of different lengths. Passing the container length
  //     to `load()` makes wavesurfer stretch the timeline past the
  //     audio buffer, leaving an empty tail.
  // Strategy: clear any stale duration option, kick off `load(url)` with
  // no preset duration, and listen once for `decode` — its first arg
  // *is* the AudioBuffer's duration. Force a redraw against that value
  // and forward it to the consumer.
  const loadSpeakerAudio = useCallback((): void => {
    if (!isSpeaker || !audioUrlOverride) return;
    const wavesurfer = avComponentRef.current?.audioRef;
    if (!wavesurfer) return;
    if (speakerLoadedUrlRef.current === audioUrlOverride) return;
    speakerLoadedUrlRef.current = audioUrlOverride;

    const url = audioUrlOverride;

    // Clear the dummy-load-seeded `options.duration` so wavesurfer
    // doesn't clamp peaks to 300s during the brief window between load
    // dispatch and decode-event resolution.
    try {
      (wavesurfer as unknown as { setOptions?(opts: { duration: undefined }): void }).setOptions?.({
        duration: undefined,
      });
    } catch {
      // ignore — setOptions is best-effort
    }

    const handleDecode = (decodedDuration?: number): void => {
      if (speakerLoadedUrlRef.current !== url) return;
      let resolved =
        Number.isFinite(decodedDuration) && (decodedDuration ?? 0) > 0
          ? (decodedDuration as number)
          : 0;
      if (!resolved) {
        // Last-resort fallbacks: wavesurfer's own getDuration, then the
        // underlying media element's natural duration.
        const fromGet = wavesurfer.getDuration();
        if (Number.isFinite(fromGet) && fromGet > 0) {
          resolved = fromGet;
        } else {
          const media = (
            wavesurfer as unknown as { getMediaElement?(): HTMLMediaElement | null }
          ).getMediaElement?.();
          if (media && Number.isFinite(media.duration) && media.duration > 0) {
            resolved = media.duration;
          }
        }
      }
      if (resolved > 0) {
        try {
          (wavesurfer as unknown as { setOptions?(opts: { duration: number }): void }).setOptions?.(
            { duration: resolved },
          );
        } catch (err) {
          console.warn('[useAudioLoader] setOptions(duration) failed', err);
        }
      }
      try {
        onSpeakerWaveReadyRef.current?.(resolved);
      } catch (err) {
        console.error('[useAudioLoader] onSpeakerWaveReady threw', err);
      }
    };

    const unsubscribeDecode = wavesurfer.once('decode', handleDecode);
    // Cached / fast-loading URLs may emit `ready` before `decode`.
    // Capture whatever duration is available there too — handleDecode is
    // idempotent under monotonic durations on the consumer side.
    const unsubscribeReady = wavesurfer.once('ready', () => {
      const ready = wavesurfer.getDuration();
      if (Number.isFinite(ready) && ready > 0) handleDecode(ready);
    });

    wavesurfer.load(url).catch((err: unknown) => {
      console.error('[useAudioLoader] speaker waveform load failed', err);
      try {
        unsubscribeDecode();
        unsubscribeReady();
      } catch (_unsubErr) {
        // intentional no-op
      }
    });
  }, [isSpeaker, audioUrlOverride]);

  useEffect(() => {
    loadSpeakerAudio();
  }, [loadSpeakerAudio]);

  const onAvComponentReady = useCallback(
    (data: AVComponent): void => {
      avComponentRef.current = data;
      if (isSpeaker) {
        loadSpeakerAudio();
        return;
      }
      initWaveform().catch(console.error);

      // Apply any buffered peak data now that WaveSurfer is available
      if (needsUpdateRef.current && data.audioRef) {
        pdp.updateWavesurfer(data.audioRef).catch(console.error);
        needsUpdateRef.current = false;
      }
    },
    [initWaveform, pdp, isSpeaker, loadSpeakerAudio],
  );

  const handleStop = useCallback(() => {
    if (socketPromise) {
      socketPromise.off('peak_data');
      subscribedRef.current = false;
    }
  }, [socketPromise]);

  return { onAvComponentReady, handleStop, peakDataProcessor: pdp };
}
